import React, { useState, useEffect, useRef } from 'react';

import { isRowValid } from './helpers';

import {
    Box,
    Checkbox,
    TextField,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
} from "@mui/material";

import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import ClearIcon from '@mui/icons-material/Clear';

const AddStudentsTable = ( { addMethod, students, setStudents } ) => {
    const [editableCell, setEditableCell] = useState(null);
    const lastStudentEmpty = students.length > 0 ? students[students.length - 1].name === "" && students[students.length - 1].email === "" : false;
    const focusedTextField = useRef();

    const defaultStudents = [
        { name: "", email: "", valid: false }
    ];

    const handleStudentChange = (event, index) => {
        const newStudents = students.map((student, i) => {
            if (i === index) {
                const name = event.target.name === 'name' ? event.target.value : student.name;
                const email = event.target.name === 'email' ? event.target.value : student.email;
                // Spread to preserve fields that are not edited here, notably
                // an imported password.
                const updated = { ...student, name, email };
                return { ...updated, valid: isRowValid(updated) };
            }
            return student;
        });
        setStudents(newStudents);
    };

    const handleNoEmailChange = (event, index) => {
        const newStudents = students.map((student, i) => {
            if (i === index) {
                // Any email stays in the row (so unticking restores it); the
                // wizard's payload mapping drops it for no-email students.
                const updated = { ...student, noEmail: event.target.checked };
                return { ...updated, valid: isRowValid(updated) };
            }
            return student;
        });
        setStudents(newStudents);
    };

    // The header checkbox operates only on rows without an email address —
    // rows that have one are never bulk-converted to generated accounts —
    // and ignores the trailing empty row.
    const isNoEmailCandidate = (student, index) =>
        !student.email && !(lastStudentEmpty && index === students.length - 1);
    const noEmailCandidates = students.filter(isNoEmailCandidate);
    const allCandidatesMarked = noEmailCandidates.length > 0 && noEmailCandidates.every(student => student.noEmail);
    const someCandidatesMarked = noEmailCandidates.some(student => student.noEmail);

    const handleNoEmailAllChange = (event) => {
        const noEmail = event.target.checked;
        setStudents(students.map((student, index) => {
            if (!isNoEmailCandidate(student, index)) return student;
            const updated = { ...student, noEmail };
            return { ...updated, valid: isRowValid(updated) };
        }));
    };

    const addStudentRow = () => {
        setEditableCell({ row: students.length, cell: "name" });
        setStudents([...students, ...defaultStudents]);
    };

    useEffect(() => {
        if (addMethod === 'manual') {
            addStudentRow();
        }
    }, [addMethod]);

    useEffect(() => {
        if (focusedTextField.current) focusedTextField.current.focus();
    }, [editableCell]);

    const handleDelete = (indexToDelete) => {
        if (indexToDelete === 0 && students.length === 1) {
            setStudents(defaultStudents);
        } else {
            setStudents(students.filter((_, index) => index !== indexToDelete));
        }
    };

    const handleKeyDown = (event, nextRowIndex, nextCellName) => {
        if (event.key === 'Enter' || event.key === 'Escape') {
            event.target.blur();
        } else if (event.key === 'Tab') {
            event.preventDefault();
            setEditableCell({ row: nextRowIndex, cell: nextCellName });
        }
    };

    return (
        <TableContainer component={Paper} sx={{ maxHeight: 240 }}>
            <Table stickyHeader sx={{ minWidth: 400, tableLayout: 'fixed' }}>
                <colgroup>
                    <col style={{ width: '30px' }} />
                    <col />
                    <col />
                    <col style={{ width: '80px' }} />
                    <col style={{ width: '73px' }} />
                </colgroup>
                <TableHead>
                    <TableRow>
                        <TableCell>#</TableCell>
                        <TableCell>Name</TableCell>
                        <TableCell>Email Address</TableCell>
                        <TableCell align="center">
                            No email
                            {noEmailCandidates.length > 0 &&
                                <Checkbox
                                    checked={allCandidatesMarked}
                                    indeterminate={someCandidatesMarked && !allCandidatesMarked}
                                    onChange={handleNoEmailAllChange}
                                    size="small"
                                    sx={{ p: '2px' }}
                                    inputProps={{ 'aria-label': 'Mark all students without an email address' }}
                                />
                            }
                        </TableCell>
                        <TableCell></TableCell>
                    </TableRow>
                </TableHead>
                <TableBody sx={{ overflowY: 'auto', maxHeight: 200 }}>
                    {students.map((student, index) => {
                        const isFocusedName = editableCell?.row === index && editableCell?.cell === 'name';
                        const isFocusedEmail = editableCell?.row === index && editableCell?.cell === 'email';
                        const showErrors = editableCell?.row !== index && !(lastStudentEmpty && index === students.length - 1);
                        return (
                            <TableRow key={index}>
                                <TableCell>{index + 1}</TableCell>
                                <TableCell>
                                    {!student.name || isFocusedName ? (
                                        <TextField
                                            inputRef={ isFocusedName ? focusedTextField : null }
                                            name="name"
                                            value={student.name}
                                            margin="none"
                                            variant="standard"
                                            placeholder="e.g. John Dayton"
                                            fullWidth={true}
                                            inputProps={{ style: { fontSize: '1rem', padding: '7px' } }}
                                            onChange={(event) => handleStudentChange(event, index)}
                                            onFocus={() => setEditableCell({ row: index, cell: 'name' })}
                                            onBlur={() => setEditableCell(null)}
                                            onKeyDown={(event) => student.noEmail
                                                ? handleKeyDown(event, index + 1, 'name')
                                                : handleKeyDown(event, index, 'email')}
                                            error={showErrors && !student.name}
                                            autoComplete="off"
                                        />
                                    ) : (
                                        <Box onClick={() => setEditableCell({ row: index, cell: 'name' })} sx={{ mt: '2px', mb: '1px' }}>
                                            {student.name || 'Click to edit'}
                                        </Box>
                                    )}
                                </TableCell>
                                <TableCell>
                                    {student.noEmail ? (
                                        <Box sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                                            Will be generated
                                        </Box>
                                    ) : !student.valid || isFocusedEmail ? (
                                        <TextField
                                            inputRef={ isFocusedEmail ? focusedTextField : null }
                                            name="email"
                                            value={student.email}
                                            margin="none"
                                            variant="standard"
                                            placeholder="e.g. john@school.com"
                                            fullWidth={true}
                                            inputProps={{ style: { fontSize: '1rem', padding: '7px' } }}
                                            onChange={(event) => handleStudentChange(event, index)}
                                            onFocus={() => setEditableCell({ row: index, cell: 'email' })}
                                            onBlur={() => setEditableCell(null)}
                                            onKeyDown={(event) => handleKeyDown(event, index + 1, 'name')}
                                            error={showErrors && !student.valid}
                                            autoComplete="off"
                                        />
                                    ) : (
                                        <Box onClick={() => setEditableCell({ row: index, cell: 'email' })}>
                                            {student.email || 'Click to edit'}
                                        </Box>
                                    )}
                                </TableCell>
                                <TableCell align="center">
                                    <Checkbox
                                        checked={!!student.noEmail}
                                        onChange={(event) => handleNoEmailChange(event, index)}
                                        size="small"
                                        inputProps={{ 'aria-label': `No email address for ${student.name || `student ${index + 1}`}` }}
                                    />
                                </TableCell>
                                <TableCell>
                                    { (students.length !== 1 || !lastStudentEmpty) &&
                                            <IconButton onClick={() => handleDelete(index)} color="error">
                                                <ClearIcon fontSize="small" />
                                            </IconButton>
                                    }
                                </TableCell>
                            </TableRow>
                        );
                    })}
                        <TableRow>
                            <TableCell colSpan={5} style={{ textAlign: 'right' }}>
                                <IconButton onClick={addStudentRow} disabled={lastStudentEmpty} sx={{ color: '#42D16A' }}>
                                    <AddCircleOutlineIcon />
                                </IconButton>
                            </TableCell>
                        </TableRow>
                </TableBody>
            </Table>
        </TableContainer>
    );
};

export default AddStudentsTable;
